import {
  hasPermissions,
  getUserPermissions,
  getCurrentAccount,
  getUserRole,
} from './permissionsHelper';

import { getSidebarStructure } from 'dashboard/constants/sidebarStructure';
import { DEFAULT_ROLE_PERMISSIONS } from 'dashboard/constants/permissions.js';

import {
  ROLES,
  CONVERSATION_PERMISSIONS,
  CONTACT_PERMISSIONS,
  REPORTS_PERMISSIONS,
  PORTAL_PERMISSIONS,
} from 'dashboard/constants/permissions.js';

// Built once at module load; keys serve as identity — labels are irrelevant here.
const SIDEBAR_STRUCTURE = getSidebarStructure(key => key);
const flatten = items =>
  items.flatMap(item => [
    item,
    ...(item.children ? flatten(item.children) : []),
  ]);
const SIDEBAR_NODES = flatten(SIDEBAR_STRUCTURE);

const SIDEBAR_PATH_MATCHERS = [
  { key: 'contacts', regex: /(^|\/)contacts(\/|$)/ },
  { key: 'companies', regex: /(^|\/)companies(\/|$)/ },
  { key: 'reports', regex: /(^|\/)reports(\/|$)/ },
  { key: 'captain', regex: /(^|\/)captain(\/|$)/ },
  { key: 'campaigns', regex: /(^|\/)campaigns(\/|$)/ },
  { key: 'sales_assistant', regex: /(^|\/)sales-assist(\/|$)/ },
  { key: 'portals', regex: /(^|\/)portals(\/|$)/ },
  { key: 'settings', regex: /(^|\/)settings(\/|$)/ },
  {
    key: 'conversation',
    regex:
      /(^|\/)(dashboard|mentions|unattended|participating|inbox|label|team|custom_view)(\/|$)/,
  },
];

const normalizeRoutePath = route => {
  const rawPath = route?.path || route?.fullPath || '';
  return rawPath.split('?')[0].split('#')[0];
};

const resolveSidebarRouteName = route => {
  if (typeof route === 'string') return route;
  return route?.params?.navigationPath || route?.name || '';
};

export const routeIsAccessibleFor = (route, userPermissions = []) => {
  const { meta: { permissions: routePermissions = [] } = {} } = route;
  return hasPermissions(routePermissions, userPermissions);
};

export const defaultRedirectPage = (to, permissions) => {
  const { accountId } = to.params;

  const permissionRoutes = [
    {
      permissions: [...ROLES, ...CONVERSATION_PERMISSIONS],
      path: 'dashboard',
    },
    { permissions: [CONTACT_PERMISSIONS], path: 'contacts' },
    { permissions: [REPORTS_PERMISSIONS], path: 'reports/overview' },
    { permissions: [PORTAL_PERMISSIONS], path: 'portals' },
  ];

  const route = permissionRoutes.find(({ permissions: routePermissions }) =>
    hasPermissions(routePermissions, permissions)
  );

  return `accounts/${accountId}/${route ? route.path : 'dashboard'}`;
};

export const isRouteAccessibleBySidebarPermissions = (
  route,
  currentAccount,
  user
) => {
  const routeName = resolveSidebarRouteName(route);
  const routePath = normalizeRoutePath(route);

  const getSidebarRouteMatch = ({ name, path }) => {
    const matchedNode = SIDEBAR_NODES.find(
      node => node.toName === name || node.activeOn?.includes(name)
    );

    if (matchedNode) {
      return { matchedKey: matchedNode.key };
    }

    if (path) {
      const pathMatch = SIDEBAR_PATH_MATCHERS.find(({ regex }) =>
        regex.test(path)
      );
      if (pathMatch) {
        return { matchedKey: pathMatch.key };
      }
    }

    return null;
  };

  const role = getUserRole(user, currentAccount.id);
  if (role === 'administrator') return true;

  const sidebarPermissions =
    currentAccount.settings?.role_permissions || DEFAULT_ROLE_PERMISSIONS;
  const userRolePermissions = sidebarPermissions[role] || [];

  const match = getSidebarRouteMatch({ name: routeName, path: routePath });
  if (!match) return true; // Route not in sidebar structure, allow access

  return userRolePermissions.includes(match.matchedKey);
};

const isRoleManagedRoute = route => {
  const routeName = resolveSidebarRouteName(route);
  const routePath = normalizeRoutePath(route);

  if (
    SIDEBAR_NODES.some(
      node => node.toName === routeName || node.activeOn?.includes(routeName)
    )
  ) {
    return true;
  }

  return SIDEBAR_PATH_MATCHERS.some(({ regex }) => regex.test(routePath));
};

const validateActiveAccountRoutes = (to, user, currentAccount) => {
  // If the current account is active, then check for the route permissions
  const accountDashboardURL = `accounts/${to.params.accountId}/dashboard`;

  // If the user is trying to access suspended route, redirect them to dashboard
  if (to.name === 'account_suspended') {
    return accountDashboardURL;
  }

  // If the user is trying to access sales assist routes and sales assist is inactive, redirect to dashboard
  const salesAssistRoutes = ['sales_assist_index', 'sales_assist_conversation'];
  if (salesAssistRoutes.includes(to.name)) {
    if (currentAccount?.settings?.sales_assist_enabled === 'inactive') {
      return accountDashboardURL;
    }
  }

  const role = getUserRole(user, currentAccount.id);
  const roleManagedRoute = isRoleManagedRoute(to);

  if (roleManagedRoute) {
    if (!isRouteAccessibleBySidebarPermissions(to, currentAccount, user)) {
      return accountDashboardURL;
    }

    // Sidebar permissions are the source of truth for manager/agent on sidebar-managed routes.
    if (role === 'manager' || role === 'agent') {
      return null;
    }
  }

  const userPermissions = getUserPermissions(user, to.params.accountId);

  const isAccessible = routeIsAccessibleFor(to, userPermissions);
  if (!isAccessible) {
    return defaultRedirectPage(to, userPermissions);
  }

  return null;
};

export const validateLoggedInRoutes = (to, user) => {
  const accountId = Number(to.params.accountId);
  const currentAccountFromMeta = to.meta?.currentAccount;
  const currentAccount = currentAccountFromMeta?.id
    ? currentAccountFromMeta
    : getCurrentAccount(user, accountId);
  // If current account is missing, either user does not have
  // access to the account or the account is deleted, return to login screen
  if (!currentAccount) {
    return `app/login`;
  }

  const isCurrentAccountActive = currentAccount.status === 'active';

  if (isCurrentAccountActive) {
    return validateActiveAccountRoutes(to, user, currentAccount);
  }

  // If the current account is not active, only the suspended screen is
  // reachable; administrators can also access billing to restore the account
  const userPermissions = getUserPermissions(user, to.params.accountId);
  const accessibleRoutes = userPermissions.includes('administrator')
    ? ['account_suspended', 'billing_settings_index']
    : ['account_suspended'];

  if (!accessibleRoutes.includes(to.name)) {
    return `accounts/${to.params.accountId}/suspended`;
  }

  // Proceed to the route if none of the above conditions are met
  return null;
};

export const isAConversationRoute = (
  routeName,
  includeBase = false,
  includeExtended = true
) => {
  const baseRoutes = [
    'home',
    'conversation_mentions',
    'conversation_unattended',
    'inbox_dashboard',
    'label_conversations',
    'team_conversations',
    'folder_conversations',
    'conversation_participating',
  ];
  const extendedRoutes = [
    'inbox_conversation',
    'conversation_through_mentions',
    'conversation_through_unattended',
    'conversation_through_inbox',
    'conversations_through_label',
    'conversations_through_team',
    'conversations_through_folders',
    'conversation_through_participating',
  ];

  const routes = [
    ...(includeBase ? baseRoutes : []),
    ...(includeExtended ? extendedRoutes : []),
  ];

  return routes.includes(routeName);
};

export const getConversationDashboardRoute = routeName => {
  switch (routeName) {
    case 'inbox_conversation':
      return 'home';
    case 'conversation_through_mentions':
      return 'conversation_mentions';
    case 'conversation_through_unattended':
      return 'conversation_unattended';
    case 'conversations_through_label':
      return 'label_conversations';
    case 'conversations_through_team':
      return 'team_conversations';
    case 'conversations_through_folders':
      return 'folder_conversations';
    case 'conversation_through_participating':
      return 'conversation_participating';
    case 'conversation_through_inbox':
      return 'inbox_dashboard';
    default:
      return null;
  }
};

export const isAInboxViewRoute = (routeName, includeBase = false) => {
  const baseRoutes = ['inbox_view'];
  const extendedRoutes = ['inbox_view_conversation'];
  const routeNames = includeBase
    ? [...baseRoutes, ...extendedRoutes]
    : extendedRoutes;
  return routeNames.includes(routeName);
};

export const isUpgradePageBypassRoute = routeName =>
  [
    'billing_settings_index',
    'settings_inbox_list',
    'general_settings_index',
    'agent_list',
  ].includes(routeName);
